"use client";

import useProfile from "@/modules/profile/api/useProfile";
import { BusinessItem } from "@/services/businessService";
import { queryClient } from "@/shared/services/react-query";
import { ContactType } from "@prisma/client";
import {
    TbArrowLeft,
    TbArrowUpRight,
    TbBrandBehance,
    TbBrandDribbble,
    TbBrandFacebook,
    TbBrandGithub,
    TbBrandInstagram,
    TbBrandLine,
    TbBrandLinkedin,
    TbBrandMedium,
    TbBrandPinterest,
    TbBrandSpotify,
    TbBrandTelegram,
    TbBrandThreads,
    TbBrandTiktok,
    TbBrandWhatsapp,
    TbBrandX,
    TbBrandYoutube,
    TbLink,
    TbMail,
    TbMapPin,
    TbSettings,
} from "react-icons/tb";
import { QueryClientProvider } from "react-query";
import { contactTypeOptions } from "../data/business-contact-data";
import { useSearchParams } from 'next/navigation';

const SocialIcon = ({ icon }: { icon: ContactType }) => {
    const paths: Record<ContactType, JSX.Element> = {
        instagramUsername: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background:
                        "linear-gradient(135deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)",
                }}
            >
                <TbBrandInstagram className="size-8" />
            </div>
        ),
        tiktokUsername: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#000000",
                }}
            >
                <TbBrandTiktok className="size-9" />
            </div>
        ),
        youTubeUrl: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#FF0000",
                }}
            >
                <TbBrandYoutube className="size-9" />
            </div>
        ),
        twitterUsername: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#000000",
                }}
            >
                <TbBrandX className="size-9" />
            </div>
        ),
        facebookUrl: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#1877F2",
                }}
            >
                <TbBrandFacebook className="size-9" />
            </div>
        ),
        whatsappNumber: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#25D366",
                }}
            >
                <TbBrandWhatsapp className="size-9" />
            </div>
        ),
        telegramUsername: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background:
                        "linear-gradient(135deg, #27A7E7 0%, #229ED9 100%)",
                }}
            >
                <TbBrandTelegram className="size-9" />
            </div>
        ),
        lineUsername: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#06C755",
                }}
            >
                <TbBrandLine className="size-9" />
            </div>
        ),
        linkedinUsername: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#0A66C2",
                }}
            >
                <TbBrandLinkedin className="size-9" />
            </div>
        ),
        spotifyUrl: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#1DB954",
                }}
            >
                <TbBrandSpotify className="size-9" />
            </div>
        ),
        pinterestUsername: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#E60023",
                }}
            >
                <TbBrandPinterest className="size-9" />
            </div>
        ),
        githubUsername: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#181717",
                }}
            >
                <TbBrandGithub className="size-9" />
            </div>
        ),
        website: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#229ED9",
                }}
            >
                <TbLink className="size-9" />
            </div>
        ),
        threadsUsername: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#000000",
                }}
            >
                <TbBrandThreads className="size-9" />
            </div>
        ),
        gmapsUrl: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background:
                        "linear-gradient(135deg, #34A853 0%, #4285F4 50%, #FBBC05 100%)",
                }}
            >
                <TbMapPin className="size-9" />
            </div>
        ),
        address: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background:
                        "linear-gradient(135deg, #34A853 0%, #4285F4 50%, #FBBC05 100%)",
                }}
            >
                <TbMapPin className="size-9" />
            </div>
        ),
        mediumUrl: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#12100E",
                }}
            >
                <TbBrandMedium className="size-9" />
            </div>
        ),
        behanceUrl: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#1769FF",
                }}
            >
                <TbBrandBehance className="size-9" />
            </div>
        ),
        dribbbleUrl: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#EA4C89",
                }}
            >
                <TbBrandDribbble className="size-9" />
            </div>
        ),
        email: (
            <div
                className="rounded-xl text-white p-1"
                style={{
                    background: "#EA4C89",
                }}
            >
                <TbMail className="size-9" />
            </div>
        ),
    };
    return <>{paths[icon]}</>;
};

export default function BusinessHome({ business }: { business: BusinessItem }) {
    return (
        <QueryClientProvider client={queryClient}>
            <LinkInBioPage business={business} />
        </QueryClientProvider>
    );
}

function LinkInBioPage({ business }: { business: BusinessItem }) {
    const profile = useProfile();
    const searchParams = useSearchParams();
    const isPreview = searchParams.get('isPreview') === '1';
    return (
        <>
            {profile.data?.id === business.userId &&
                !isPreview && (
                    <div className="w-full h-[55px] flex items-center gap-2 text-white bg-[#111] sticky top-0 z-99 mb-2 px-6">
                        <a
                            href={`https://creator.tujuhub.id/${business.id}`}
                            className="inline-flex items-center gap-1 p-2 px-3 pe-4 hover:bg-[#222] transition duration-200 ease-in-out hover:border-white rounded-xl border border-[#555] text-white text-sm"
                        >
                            <TbArrowLeft className="size-4.5" /> Dashboard
                        </a>
                        <a
                            href={`https://creator.tujuhub.id/${business.id}/settings/general`}
                            className="inline-flex items-center gap-1.5 p-2 px-3 pe-4 hover:bg-[#222] transition duration-200 ease-in-out rounded-xl text-white text-sm"
                        >
                            <TbSettings className="size-4.5" /> Atur homepage
                        </a>
                    </div>
                )}
            <div className="flex flex-col gap-8 min-h-[100vh]">
                <header>
                    <div className="max-w-5xl mx-auto px-5 pt-5 flex flex-col gap-3.5 lg:gap-4.5">
                        <div
                            style={
                                business.coverSrc
                                    ? {
                                          backgroundImage: `url(${business.coverSrc})`,
                                          backgroundSize: "cover",
                                          backgroundPosition: "center",
                                      }
                                    : {
                                          backgroundImage: "url(/img/bg.webp)",
                                          backgroundSize: "100% auto",
                                      }
                            }
                            className="w-full aspect-[3.5/1] bg-default-300 rounded-2xl lg:rounded-3xl relative"
                        >
                            <div className="absolute left-0 top-0 w-full h-full bg-[rgba(0,0,0,0.5)] rounded-2xl lg:rounded-3xl"></div>
                            <div
                                style={
                                    business.logoSrc
                                        ? {}
                                        : {
                                              backgroundImage:
                                                  "url(/img/geometri.avif)",
                                              backgroundSize: "200%",
                                              backgroundPosition: "center",
                                          }
                                }
                                className={`size-20 lg:size-25 rounded-full absolute left-[50%] bottom-0 translate-x-[-50%] translate-y-[40%] shadow-lg ${business.logoSrc ? "bg-background" : ""}`}
                            >
                                {business.logoSrc && (
                                    <img
                                        src={business.logoSrc}
                                        alt={business.name}
                                        className="w-full h-full object-center object-cover rounded-full"
                                    />
                                )}
                            </div>
                        </div>

                        <div className="pt-7.5 lg:pt-9 text-center">
                            <h1 className="text-2xl font-semibold">
                                {business.name}
                            </h1>
                            {business.bio && (
                                <div className="mt-2.5 lg:mt-4 text-default-500 text-sm lg:text-[16px] whitespace-pre-line">
                                    {business.bio}
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <section>
                    <div className="max-w-5xl mx-auto px-5">
                        {business.contacts && business.contacts.length > 0 && (
                            <div className="w-full flex items-center justify-center">
                                <div className="gap-2.5 inline-flex items-center justify-center">
                                    {contactTypeOptions.map((type) => {
                                        const contacts =
                                            business.contacts?.filter(
                                                (contact) =>
                                                    contact.type === type.key,
                                            ) || [];
                                        return contacts.length > 0 && (
                                            <>
                                                {contacts.map((social) => {
                                                    const type = social.type;
                                                    const icon = type ? (
                                                        SocialIcon({
                                                            icon: type,
                                                        })
                                                    ) : (
                                                        <></>
                                                    );
                                                    let url =
                                                        type ===
                                                        "whatsappNumber"
                                                            ? "https://wa.me/"
                                                            : type ===
                                                                "instagramUsername"
                                                              ? "https://instagram.com/"
                                                              : type ===
                                                                  "tiktokUsername"
                                                                ? "https://tiktok.com/"
                                                                : type ===
                                                                    "youTubeUrl"
                                                                  ? "https://youtube.com/"
                                                                  : type ===
                                                                      "twitterUsername"
                                                                    ? "https://x.com/"
                                                                    : type ===
                                                                        "facebookUrl"
                                                                      ? "https://facebook.com/"
                                                                      : type ===
                                                                          "telegramUsername"
                                                                        ? "https://t.me/"
                                                                        : type ===
                                                                            "lineUsername"
                                                                          ? "https://line.me/ti/p/"
                                                                          : type ===
                                                                              "linkedinUsername"
                                                                            ? "https://linkedin.com/in/"
                                                                            : type ===
                                                                                "spotifyUrl"
                                                                              ? ""
                                                                              : type ===
                                                                                  "pinterestUsername"
                                                                                ? "https://pinterest.com/"
                                                                                : type ===
                                                                                    "githubUsername"
                                                                                  ? "https://github.com/"
                                                                                  : type ===
                                                                                      "threadsUsername"
                                                                                    ? "https://www.threads.net/"
                                                                                    : type ===
                                                                                        "email"
                                                                                      ? "mailto:"
                                                                                      : "";
                                                    url += social.value || "#";
                                                    return (
                                                        icon && (
                                                            <a
                                                                href={url}
                                                                title={
                                                                    social.type ||
                                                                    ""
                                                                }
                                                                key={
                                                                    social.type
                                                                }
                                                                target="_blank"
                                                                className="text-default-500 hover:scale-[1.1] hover:rotate-[5deg] transition-all duration-200 ease-in-out"
                                                            >
                                                                {icon}
                                                            </a>
                                                        )
                                                    );
                                                })}
                                            </>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {business.homepageLinks.length > 0 && (
                    <section>
                        <div className="max-w-5xl mx-auto px-5 grid grid-cols-1 lg:grid-cols-3 gap-3">
                            {business.homepageLinks.map((link, i) => (
                                <a
                                    key={i}
                                    href={link.link || "#"}
                                    className="p-4 rounded-3xl bg-default-50 p-1 flex flex-col gap-3.5 relative group hover:bg-default-100 transition-all duration-200 ease-in-out"
                                >
                                    {link.imageUrl ? (
                                        <div className="bg-default-900 aspect-[3/2] rounded-2xl">
                                            <img
                                                src={link.imageUrl}
                                                className="w-full h-full object-contain object-center rounded-2xl"
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="w-full aspect-[3/2] hidden lg:flex bg-[#111] rounded-2xl items-center justify-center">
                                                {business.logoSrc ? (
                                                    <img
                                                        src={business.logoSrc}
                                                        alt={business.name}
                                                        className="avatar-ring size-12"
                                                        style={{
                                                            borderRadius: "50%",
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="size-12 rounded-full"
                                                        style={{
                                                            backgroundImage:
                                                                "url(/img/geometri.avif)",
                                                            backgroundSize:
                                                                "200%",
                                                            backgroundPosition:
                                                                "center",
                                                        }}
                                                    ></div>
                                                )}
                                            </div>
                                            <TbArrowUpRight className="size-5 lg:hidden absolute right-3.5 top-3.5 text-default-500 group-hover:scale-[1.2] transition-all duration-100 ease-in-out group-hover:right-3 group-hover:rotate-[5deg] group-hover:top-3" />
                                        </>
                                    )}
                                    <div className="pe-8 lg:pe-0">
                                        <h3 className="font-medium group-hover:underline leading-[1.3]">
                                            {link.title}
                                        </h3>
                                        {link.description && (
                                            <div className="text-default-500 text-sm mt-1.5 leading-[1.4]">
                                                {link.description}
                                            </div>
                                        )}
                                    </div>
                                </a>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            <footer className="pb-5 pt-10 text-default-700 text-center flex items-center gap-2 justify-center">
                Powered by{" "}
                <a
                    href="https://tujuhub.id"
                    target="_blank"
                    className="font-semibold flex items-center gap-1"
                >
                    <img
                        src="/img/logo-circle.png"
                        alt="logo"
                        className="size-6"
                    />{" "}
                    Tujuhub
                </a>
            </footer>
        </>
    );
}
